/**
 * Pure geometry for Design Huddle (DH) logo element frames, in the inch space DH reports for an
 * element (`width`/`height`/`left`/`top`) and its print zone.
 *
 * WHY THIS EXISTS: DH renders a VECTOR element by filling its frame. When the media on an existing
 * element is swapped (in-editor upload, DH's own Replace, drag-and-drop) WITHOUT re-fitting the
 * frame to the new artwork's aspect ratio, the artwork is scaled non-uniformly to the old frame —
 * a 1.8:1 oval logo dropped into the template's square 2×2in placeholder exports as a 1:1 circle.
 * The on-screen thumbnail is rendered from a separate project seeded via createProject, where DH
 * does fit the frame to the asset, so the defect is invisible until the print file reaches
 * production. See specs/investigations/2026-09-30-dh-print-file-logo-stretched-into-element-frame.md.
 *
 * No Angular dependencies — shared by the upload override (re-fit on replace) and the designer's
 * export guard (block the print export when the frame and asset disagree).
 */
export interface Frame {
  width: number;
  height: number;
  left: number;
  top: number;
}

/** Tolerance on |frameAspect / assetAspect − 1| above which a frame counts as distorted. Rows that
 *  genuinely match sat within 2% in production; a 1.0 frame on a 1.009 (square) logo must pass. */
export const FRAME_ASPECT_TOLERANCE = 0.05;

const ROUND_DECIMALS = 3;

function isPositiveFinite(value: any): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function round(value: number): number {
  const factor = Math.pow(10, ROUND_DECIMALS);
  return Math.round(value * factor) / factor;
}

function isUsableFrame(frame: Frame | null | undefined): frame is Frame {
  return !!frame
    && isPositiveFinite(frame.width)
    && isPositiveFinite(frame.height)
    && Number.isFinite(frame.left)
    && Number.isFinite(frame.top);
}

/**
 * Re-fits `frame` to `assetAspect` (= assetWidth / assetHeight): keeps the frame's width, derives
 * the height from the aspect, and preserves the frame's centre. When a `zone` is given and the
 * fitted frame would exceed it, the frame is scaled down uniformly to fit, then its position is
 * clamped inside the zone (the same "never stack past the zone" rule as
 * PlacementGeometry.ScaleElementWithinZone in the API).
 *
 * Returns the input unchanged when the frame or aspect is not usable, so callers can always send
 * the result without special-casing bad input.
 */
export function fitFrameToAspect(frame: Frame, assetAspect: number, zone?: Frame | null): Frame {
  if (!isUsableFrame(frame) || !isPositiveFinite(assetAspect)) {
    return frame;
  }

  const centerX = frame.left + frame.width / 2;
  const centerY = frame.top + frame.height / 2;

  let width = frame.width;
  let height = width / assetAspect;

  const hasZone = isUsableFrame(zone);
  if (hasZone) {
    if (height > zone.height) {
      height = zone.height;
      width = height * assetAspect;
    }
    if (width > zone.width) {
      width = zone.width;
      height = width / assetAspect;
    }
  }

  let left = centerX - width / 2;
  let top = centerY - height / 2;

  if (hasZone) {
    left = Math.max(zone.left, Math.min(left, zone.left + zone.width - width));
    top = Math.max(zone.top, Math.min(top, zone.top + zone.height - height));
  }

  return { width: round(width), height: round(height), left: round(left), top: round(top) };
}

/**
 * True when the frame's aspect ratio differs from the asset's by more than `tolerance`.
 * False (never block) on unusable input — a missing frame or unknown asset size cannot be judged.
 */
export function isFrameDistorted(frame: Frame | null | undefined, assetAspect: number, tolerance: number = FRAME_ASPECT_TOLERANCE): boolean {
  if (!isUsableFrame(frame) || !isPositiveFinite(assetAspect)) {
    return false;
  }
  const frameAspect = frame.width / frame.height;
  return Math.abs(frameAspect / assetAspect - 1) > tolerance;
}

/**
 * Intrinsic aspect ratio (width / height) of an SVG document: explicit `width`/`height`
 * attributes first, else the `viewBox`. Mirrors resolveSvgDims() in
 * product-color-selection.component.ts. Returns null when neither is usable.
 */
export function svgAspectFromText(svgText: string): number | null {
  if (!svgText) {
    return null;
  }
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror')) {
      return null;
    }
    const svg = doc.documentElement;
    if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
      return null;
    }

    const width = parseSvgLength(svg.getAttribute('width'));
    const height = parseSvgLength(svg.getAttribute('height'));
    if (width > 0 && height > 0) {
      return width / height;
    }

    const viewBox = (svg.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
    if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
      return viewBox[2] / viewBox[3];
    }
  } catch {
    // fall through
  }
  return null;
}

/** Numeric SVG length in user units; 0 for percentages or units that cannot be resolved here. */
function parseSvgLength(value: string | null): number {
  if (!value) {
    return 0;
  }
  const match = /^\s*([0-9]*\.?[0-9]+)\s*(px)?\s*$/i.exec(value);
  return match ? Number(match[1]) : 0;
}
